import { forwardRef, useEffect, useState } from 'react'
import type { InputHTMLAttributes, KeyboardEvent } from 'react'
import { cn } from './cn'
import { IconClose, IconSearch } from './Icons'

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'size' | 'aria-label'> {
  value: string
  onChange: (value: string) => void
  /** Debounce ms before onChange fires. */
  debounceMs?: number
  /** Required accessible name; placeholder is not a label. */
  ariaLabel: string
  placeholder?: string
  /** Show clear affordance when non-empty. */
  clearable?: boolean
  /** Visual shape. */
  shape?: 'pill' | 'field'
  size?: 'sm' | 'md'
  /** Render on ink chrome. */
  ink?: boolean
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  {
    value,
    onChange,
    debounceMs = 0,
    ariaLabel,
    placeholder,
    clearable = true,
    shape = 'pill',
    size = 'md',
    ink = false,
    className,
    disabled,
    onKeyDown,
    ...props
  },
  ref,
) {
  const [draft, setDraft] = useState(value)

  useEffect(() => {
    setDraft(value)
  }, [value])

  useEffect(() => {
    if (debounceMs <= 0 || draft === value) return
    const timer = window.setTimeout(() => onChange(draft), debounceMs)
    return () => window.clearTimeout(timer)
  }, [debounceMs, draft, onChange, value])

  function commit(nextValue: string) {
    setDraft(nextValue)
    if (debounceMs <= 0) onChange(nextValue)
  }

  function clear() {
    setDraft('')
    onChange('')
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    onKeyDown?.(event)
    if (!event.defaultPrevented && event.key === 'Escape' && draft) {
      event.preventDefault()
      clear()
    }
  }

  return (
    <div
      className={cn(
        'relative flex min-w-0 items-center',
        shape === 'pill' ? 'rounded-pill' : 'rounded-control',
        className,
      )}
    >
      <IconSearch className={cn('pointer-events-none absolute left-3 shrink-0', ink ? 'text-on-ink-faint' : 'text-ink-muted')} size={18} />
      <input
        ref={ref}
        {...props}
        type="search"
        value={draft}
        disabled={disabled}
        aria-label={ariaLabel}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        onChange={(event) => commit(event.target.value)}
        className={cn(
          // Placeholder ellipsis: long mobile search hints must truncate cleanly
          // instead of hard-clipping mid-glyph at the field edge (B3 coach circuit).
          'w-full min-w-0 border text-base transition-colors duration-fast ease-out-soft placeholder:truncate placeholder:text-ink-muted focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-60',
          shape === 'pill' ? 'rounded-pill' : 'rounded-control',
          size === 'sm' ? 'min-h-control-sm py-1.5 pl-9 pr-9 text-sm' : 'min-h-control py-2.5 pl-10 pr-10',
          ink
            ? 'border-veil-line bg-veil text-on-ink placeholder:text-on-ink-faint focus-visible:shadow-focus-dark'
            : 'border-line bg-white text-ink-body hover:border-ink-soft focus:border-vjal-400',
        )}
      />
      {clearable && draft && !disabled && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={clear}
          className={cn(
            'absolute right-1 inline-flex min-h-8 min-w-8 items-center justify-center rounded-full transition-colors duration-fast ease-out-soft focus-visible:outline-none focus-visible:shadow-focus',
            ink ? 'text-on-ink-soft hover:bg-veil hover:text-on-ink focus-visible:shadow-focus-dark' : 'text-ink-muted hover:bg-whisper hover:text-strong',
          )}
        >
          <IconClose size={16} />
        </button>
      )}
    </div>
  )
})
